(() => {
  if (window.MafraProductCardLoaded) return;
  window.MafraProductCardLoaded = true;

  const SELECTORS = {
    openQuickView: '[data-mafra-open-quick-view]',
    openOptions: '[data-mafra-open-options]',
    card: '[data-mafra-card]',
    productJson: '[data-mafra-product-json]'
  };

  class MafraProductCardModal {
    constructor() {
      this.productCache = new Map();
      this.selectedVariant = null;
      this.product = null;
      this.card = null;
      this.isRtl =
        document.documentElement.dir === 'rtl' ||
        document.documentElement.lang.toLowerCase().startsWith('ar');
      this.createModal();
      this.bindEvents();
      this.primeVisibleCards();
    }

    closestFromEventTarget(target, selector) {
      const element = target instanceof Element ? target : target?.parentElement;
      return element?.closest(selector) || null;
    }

    createModal() {
      this.overlay = document.createElement('div');
      this.overlay.className = 'mafra-card-modal-overlay';
      this.overlay.setAttribute('hidden', '');

      this.modal = document.createElement('div');
      this.modal.className = 'mafra-card-modal';
      this.modal.setAttribute('hidden', '');
      this.modal.setAttribute('role', 'dialog');
      this.modal.setAttribute('aria-modal', 'true');
      this.modal.setAttribute('dir', this.isRtl ? 'rtl' : 'ltr');
      this.modal.innerHTML = `
        <div class="mafra-card-modal__content" role="document">
          <button type="button" class="mafra-card-modal__close" aria-label="${this.isRtl ? 'إغلاق' : 'Close'}">×</button>
          <div class="mafra-card-modal__body"></div>
        </div>
      `;

      document.body.appendChild(this.overlay);
      document.body.appendChild(this.modal);
      this.body = this.modal.querySelector('.mafra-card-modal__body');
    }

    bindEvents() {
      document.addEventListener('click', (event) => {
        const quickViewButton = this.closestFromEventTarget(event.target, SELECTORS.openQuickView);
        const optionsButton = this.closestFromEventTarget(event.target, SELECTORS.openOptions);

        if (quickViewButton || optionsButton) {
          event.preventDefault();
          event.stopPropagation();
          const button = quickViewButton || optionsButton;
          const card = button.closest(SELECTORS.card);
          const handle = button.dataset.productHandle || card?.dataset.productHandle;
          if (handle) this.open(handle, card);
        }
      });

      document.addEventListener('pointerenter', (event) => {
        const trigger = this.closestFromEventTarget(event.target, `${SELECTORS.openQuickView}, ${SELECTORS.openOptions}`);
        if (!trigger) return;
        const card = trigger.closest(SELECTORS.card);
        const handle = trigger.dataset.productHandle || card?.dataset.productHandle;
        if (handle) this.getProduct(handle, card).catch(() => {});
      }, true);

      document.addEventListener('focusin', (event) => {
        const trigger = this.closestFromEventTarget(event.target, `${SELECTORS.openQuickView}, ${SELECTORS.openOptions}`);
        if (!trigger) return;
        const card = trigger.closest(SELECTORS.card);
        const handle = trigger.dataset.productHandle || card?.dataset.productHandle;
        if (handle) this.getProduct(handle, card).catch(() => {});
      });

      document.addEventListener('pointerdown', (event) => {
        if (this.modal.hasAttribute('hidden')) return;
        if (typeof event.button === 'number' && event.button !== 0) return;

        const content = this.modal.querySelector('.mafra-card-modal__content');
        if (!content) return;

        const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
        const rect = content.getBoundingClientRect();
        const isInsideContent =
          path.includes(content) ||
          (
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom
          );

        if (isInsideContent) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.close();
      }, true);

      this.modal.addEventListener('click', (event) => {
        if (this.closestFromEventTarget(event.target, '.mafra-card-modal__close')) {
          event.preventDefault();
          this.close();
          return;
        }

        const variantButton = this.closestFromEventTarget(event.target, '[data-mafra-modal-variant]');
        if (variantButton) {
          event.preventDefault();
          const variantId = Number(variantButton.dataset.variantId);
          const variant = this.product?.variants?.find((item) => Number(item.id) === variantId);
          if (variant) this.selectVariant(variant);
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.modal.hasAttribute('hidden')) this.close();
      });
    }

    primeVisibleCards() {
      document.querySelectorAll(SELECTORS.card).forEach((card) => {
        const handle = card.dataset.productHandle;
        const inlineProduct = this.readInlineProduct(card);
        if (handle && inlineProduct) this.productCache.set(handle, inlineProduct);
      });
    }

    readInlineProduct(card) {
      const script = card?.querySelector(SELECTORS.productJson);
      if (!script) return null;

      try {
        const product = JSON.parse(script.textContent || '{}');
        return product && product.variants ? product : null;
      } catch (error) {
        console.warn('MA-FRA quick view: invalid product JSON', error);
        return null;
      }
    }

    async fetchProduct(handle) {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to load product: ${handle}`);
      return response.json();
    }

    async getProduct(handle, card) {
      if (this.productCache.has(handle)) return this.productCache.get(handle);

      const inlineProduct = this.readInlineProduct(card);
      if (inlineProduct) {
        this.productCache.set(handle, inlineProduct);
        return inlineProduct;
      }

      const fetchedProduct = await this.fetchProduct(handle);
      this.productCache.set(handle, fetchedProduct);
      return fetchedProduct;
    }

    async open(handle, card) {
      this.card = card || null;

      try {
        this.product = await this.getProduct(handle, card);
        const firstAvailable = this.product.variants.find((variant) => variant.available) || this.product.variants[0];
        this.selectedVariant = firstAvailable;
        this.render();
        this.show();
      } catch (error) {
        console.error(error);
        const url = card?.dataset.productUrl || `/products/${encodeURIComponent(handle)}`;
        this.body.innerHTML = `<div class="mafra-card-modal__error">Unable to open this product. <a href="${this.escapeHTML(url)}">Open the product page</a>.</div>`;
        this.show();
      }
    }

    show() {
      this.overlay.removeAttribute('hidden');
      this.modal.removeAttribute('hidden');
      document.documentElement.classList.add('mafra-card-modal-open');
    }

    close() {
      this.overlay.setAttribute('hidden', '');
      this.modal.setAttribute('hidden', '');
      document.documentElement.classList.remove('mafra-card-modal-open');
      this.body.innerHTML = '';
    }

    money(cents) {
      const value = Number(cents || 0) / 100;
      return `${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EGP`;
    }

    getShopifyImageUrl(url, width = 1400) {
      if (!url) return '';

      try {
        const fullUrl = new URL(url, window.location.origin);
        fullUrl.pathname = fullUrl.pathname.replace(
          /_(pico|icon|thumb|small|compact|medium|large|grande|[0-9]+x[0-9]*|x[0-9]+)(\.[a-zA-Z0-9]+)$/i,
          '$2'
        );
        fullUrl.searchParams.set('width', String(width));
        return fullUrl.toString();
      } catch (error) {
        const cleanUrl = String(url).replace(
          /_(pico|icon|thumb|small|compact|medium|large|grande|[0-9]+x[0-9]*|x[0-9]+)(\.[a-zA-Z0-9]+)(\?|$)/i,
          '$2$3'
        );
        const separator = cleanUrl.includes('?') ? '&' : '?';
        return `${cleanUrl}${separator}width=${width}`;
      }
    }

    getShopifyImageSrcset(url, widths = [600, 900, 1200, 1400, 1600]) {
      if (!url) return '';
      return widths
        .map((width) => `${this.getShopifyImageUrl(url, width)} ${width}w`)
        .join(', ');
    }

    escapeHTML(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    getVariantImage(variant) {
      if (variant?.featured_image?.src) return variant.featured_image.src;
      if (this.product?.featured_image) return this.product.featured_image;
      if (this.product?.images?.length) return this.product.images[0];
      return '';
    }

    getProductUrl() {
      return this.product?.url || `/products/${this.product?.handle || ''}`;
    }

    renderReviews() {
      const rating = Number(this.card?.dataset.productRating || 0);
      const ratingCount = Number(this.card?.dataset.productRatingCount || 0);
      const width = Math.max(0, Math.min(100, rating * 20));
      const ariaLabel = rating
        ? `${rating} out of 5 stars`
        : (this.isRtl ? 'لا توجد تقييمات بعد' : 'No reviews yet');

      return `
        <div class="mafra-card-modal__reviews" aria-label="${this.escapeHTML(ariaLabel)}">
          <span class="mafra-card-modal__stars" style="--rating-width:${width}%" aria-hidden="true">
            <span class="mafra-card-modal__stars-base">★★★★★</span>
            <span class="mafra-card-modal__stars-fill">★★★★★</span>
          </span>
          <span class="mafra-card-modal__review-count">(${ratingCount})</span>
        </div>
      `;
    }

    renderVariantButtons() {
      if (!this.product || this.product.variants.length <= 1) return '';
      const hasOnlyDefault = this.product.variants.length === 1 && this.product.variants[0].title === 'Default Title';
      if (hasOnlyDefault) return '';

      return `
        <div class="mafra-card-modal__variant-pills" role="list" aria-label="Product options">
          ${this.product.variants.map((variant) => {
            const selected = Number(variant.id) === Number(this.selectedVariant?.id);
            return `
              <button
                type="button"
                class="mafra-card-modal__variant-pill${selected ? ' selected' : ''}${!variant.available ? ' unavailable' : ''}"
                data-mafra-modal-variant
                data-variant-id="${this.escapeHTML(variant.id)}"
                ${!variant.available ? 'aria-disabled="true"' : ''}
              >
                ${this.escapeHTML(variant.title)}
              </button>
            `;
          }).join('')}
        </div>
      `;
    }

    render() {
      const variant = this.selectedVariant || this.product.variants[0];
      const imageRaw = this.getVariantImage(variant);
      const image = this.getShopifyImageUrl(imageRaw, 1400);
      const imageSrcset = this.getShopifyImageSrcset(imageRaw);
      const addLabel = this.card?.dataset.addLabel || (this.isRtl ? 'أضف إلى السلة' : 'Add to Cart');
      const soldLabel = this.card?.dataset.soldLabel || (this.isRtl ? 'غير متوفر' : 'Out of Stock');
      const detailsLabel = this.card?.dataset.detailsLabel || (this.isRtl ? 'عرض التفاصيل كاملة' : 'View full details');
      const compare = variant.compare_at_price && variant.compare_at_price > variant.price;
      const detailsArrow = this.isRtl ? '←' : '→';

      this.modal.setAttribute('aria-label', this.product.title || (this.isRtl ? 'عرض سريع للمنتج' : 'Product quick view'));
      this.modal.setAttribute('dir', this.isRtl ? 'rtl' : 'ltr');

      this.body.innerHTML = `
        <div class="mafra-card-modal__image-wrapper">
          ${image ? `
            <img
              src="${this.escapeHTML(image)}"
              srcset="${this.escapeHTML(imageSrcset)}"
              sizes="(max-width: 749px) 90vw, (max-width: 989px) 70vw, 560px"
              alt="${this.escapeHTML(this.product.title)}"
              class="mafra-card-modal__image"
              loading="eager"
              decoding="async"
              width="1400"
              height="1400"
            >
          ` : ''}
        </div>

        <div class="mafra-card-modal__info" dir="${this.isRtl ? 'rtl' : 'ltr'}">
          <h2 class="mafra-card-modal__title" dir="${this.isRtl ? 'rtl' : 'ltr'}">
            ${this.escapeHTML(this.product.title)}
          </h2>

          ${this.product.vendor ? `
            <div class="mafra-card-modal__vendor" dir="${this.isRtl ? 'rtl' : 'ltr'}">
              ${this.escapeHTML(this.product.vendor)}
            </div>
          ` : ''}

          ${this.renderReviews()}

          <div class="mafra-card-modal__price" dir="${this.isRtl ? 'rtl' : 'ltr'}">
            ${compare ? `
              <span class="mafra-card-modal__price-compare">${this.money(variant.compare_at_price)}</span>
            ` : ''}
            <span class="mafra-card-modal__price-current">${this.money(variant.price)}</span>
          </div>

          ${this.renderVariantButtons()}

          <div class="mafra-card-modal__actions" dir="${this.isRtl ? 'rtl' : 'ltr'}">
            <product-form class="product-form">
              <div class="product-form__error-message-wrapper" role="alert" hidden>
                <span class="product-form__error-message"></span>
              </div>
              <form
                method="post"
                action="/cart/add"
                accept-charset="UTF-8"
                class="form"
                enctype="multipart/form-data"
                novalidate="novalidate"
                data-type="add-to-cart-form"
                dir="${this.isRtl ? 'rtl' : 'ltr'}"
              >
                <input type="hidden" name="form_type" value="product">
                <input type="hidden" name="utf8" value="✓">
                <input
                  type="hidden"
                  name="id"
                  value="${this.escapeHTML(variant.id)}"
                  class="product-variant-id"
                  data-mafra-modal-variant-input
                >
                <button
                  type="submit"
                  name="add"
                  class="mafra-card-modal__cta"
                  ${variant.available ? '' : 'disabled'}
                >
                  <span>${this.escapeHTML(variant.available ? addLabel : soldLabel)}</span>
                  <span class="loading__spinner hidden" aria-hidden="true"></span>
                </button>
              </form>
            </product-form>

            <a
              href="${this.escapeHTML(this.getProductUrl())}"
              class="mafra-card-modal__details-link"
              dir="${this.isRtl ? 'rtl' : 'ltr'}"
            >
              <span>${this.escapeHTML(detailsLabel)}</span>
              <span class="mafra-card-modal__details-arrow" aria-hidden="true">${detailsArrow}</span>
            </a>
          </div>
        </div>
      `;
    }

    selectVariant(variant) {
      if (!variant.available) return;
      this.selectedVariant = variant;
      this.render();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new MafraProductCardModal());
  } else {
    new MafraProductCardModal();
  }
})();
